<template>
  <div class="dashboard">
    <!-- 头部区域 -->
    <header class="dashboard-header">
      <div class="header-left">
        <dv-decoration-8 :color="['#56829', '#000']" />
      </div>
      <div class="header-center">
        <div class="title">
          <span class="company-name">唯电能源</span>
          <span class="divider"></span>
          <span class="subtitle">数据可视化</span>
        </div>
      </div>
      <div class="header-right">
        <div class="refresh-info">
          <i class="el-icon-refresh"></i>
          <span>{{ refreshCountdown }}s 自动刷新</span>
        </div>
        <div class="fullscreen-btn" @click="toggleFullscreen">
          <i :class="isFullscreen ? 'el-icon-close' : 'el-icon-full-screen'"></i>
        </div>
        <div class="logout-btn" @click="handleLogout">
          <i class="el-icon-switch-button"></i>
        </div>
      </div>
    </header>

    <div class="dashboard-content" :class="{ fullscreen: isFullscreen }">
      <!-- 左侧面板 -->
      <aside class="left-panel">
        <!-- 统计数据 -->
        <section class="statistics-section">
          <div class="section-title">
            <i class="el-icon-data-line"></i>
            <span>核心数据</span>
          </div>
          <dv-border-box-8 ref="statisticsBorderBox">
            <div class="statistics-grid">
              <div class="stat-card" v-for="(stat, index) in statisticsList" :key="index" :class="stat.type">
                <div class="stat-content">
                  <div class="stat-main">
                    <h3>{{ stat.title }}</h3>
                    <div class="stat-value">{{ stat.value }}</div>
                  </div>
                  <div class="stat-extra">
                    <template v-if="stat.extra">
                      <template v-if="stat.type === 'status'">
                        <span class="online-count">
                          <i class="dot online"></i>在线：{{stat.extra.online ?? '-'}}
                        </span>
                        <span class="usage-rate">利用率：{{ stat.extra.usage ?? '-' }}%</span>
                      </template>
                      <template v-else-if="stat.type === 'amount'">
                        <div class="amount-row">
                          <span class="label">今日：</span>
                          <span class="value">{{ formatMoney(stat.extra.today) }}</span>
                        </div>
                        <div class="amount-row">
                          <span class="label">本月：</span>
                          <span class="value">{{ formatMoney(stat.extra.month) }}</span>
                        </div>
                      </template>
                      <template v-else>
                        <span class="active-count">活跃：{{ stat.extra.active }}</span>
                        <span class="new-count">新增：{{ stat.extra.new ? `+${stat.extra.new}` : '-' }}</span>
                      </template>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </dv-border-box-8>
        </section>

        <!-- 换电记录 -->
        <section class="records-section">
          <div class="section-title">
            <i class="el-icon-time"></i>
            <span>最新换电记录</span>
          </div>
          <dv-border-box-8 ref="recordsBorderBox">
            <div class="records-list" 
                 @mouseenter="handleMouseEnter" 
                 @mouseleave="handleMouseLeave">
              <div class="records-wrapper"
                   :style="{
                     transform: `translateY(-${scrollOffset}px)` ,
                     transition: isNoTransition ? 'none' : 'transform 0.5s cubic-bezier(0.4,0,0.2,1)'
                   }">
                <div class="record-item" v-for="(record, index) in displayRecords" :key="index">
                  <div class="record-header">
                    <span class="user-name">{{ record.realName }}</span>
                    <span class="store-name">{{ record.storeName }}</span>
                  </div>
                  <div class="record-info">
                    <div class="info-row">
                      <span class="cabinet-id">电柜编号：{{ record.cabId }}</span>
                    </div>
                    <div class="info-row battery-row">
                      <span class="battery-label">放入电池</span>
                      <span class="battery-id in">{{ record.intoPack || record.batteryIn }}</span>
                    </div>
                    <div class="info-row battery-row">
                      <span class="battery-label">吐出电池</span>
                      <span class="battery-id out">{{ record.outPack || record.batteryOut }}</span>
                    </div>
                    <div class="info-row bottom-row">
                      <span class="exchange-time">{{ formatTime(record.orderTime) }}</span>
                      <span class="exchange-result" :class="(record.status === 'success' || record.state === 1) ? 'success' : 'failed'">
                        {{ (record.status === 'success' || record.state === 1) ? '换电成功' : '换电失败' }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </dv-border-box-8>
        </section>
      </aside>

      <!-- 中间面板 -->
      <main class="center-panel">
        <!-- 设备分布图 -->
        <section class="map-section">
          <div class="section-title">
            <i class="el-icon-map-location"></i>
            <span>设备分布图</span>
          </div>
          <dv-border-box-8 ref="mapBorderBox">
            <div class="chart-box map-box">
              <div ref="distributionMapRef" class="chart-container"></div>
              <div class="map-controls">
                <div v-for="item in mapTypes" :key="item.type" 
                     :class="['map-control-item', { active: currentMapType === item.type }]"
                     @click="switchMapType(item.type)">
                  <span class="item-label">{{ item.label }}</span>
                  <span class="item-count">{{ item.count }}</span>
                </div>
              </div>
            </div>
          </dv-border-box-8>
        </section>

        <!-- 换电记录统计 -->
        <section class="chart-section">
          <div class="section-title">
            <i class="el-icon-data-analysis"></i>
            <span>换电记录统计</span>
          </div>
          <dv-border-box-8 ref="chartBorderBox">
            <div class="chart-box">
              <div ref="exchangeChartRef" class="chart-container"></div>
            </div>
          </dv-border-box-8>
        </section>
      </main>

      <!-- 右侧面板 -->
      <aside class="right-panel">
        <!-- 设备型号分布 -->
        <section class="type-section">
          <div class="section-title">
            <i class="el-icon-pie-chart"></i>
            <span>设备型号分布</span>
          </div>
          <dv-border-box-8 ref="typeBorderBox">
            <div class="type-distribution-card">
              <div class="type-charts-container">
                <div class="type-chart-item">
                  <div class="chart-title">电柜型号</div>
                  <div ref="cabinetTypeChartRef" class="chart-container"></div>
                </div>
                <div class="type-chart-item">
                  <div class="chart-title">电池型号</div>
                  <div ref="batteryTypeChartRef" class="chart-container"></div>
                </div>
              </div>
            </div>
          </dv-border-box-8>
        </section>
      </aside>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import {
  GeoComponent,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent
} from 'echarts/components'
import {
  ScatterChart,
  LineChart,
  BarChart,
  LinesChart
} from 'echarts/charts'
import { LabelLayout } from 'echarts/features'
import chinaMap from 'china-echarts-map/dist/china.js'
import { getStatistics } from '../apis'
import Cookies from 'js-cookie'
import '../styles/home.scss'
import { cityCoords } from './utils/cityCoords'

// 注册必需的组件
echarts.use([
  CanvasRenderer,
  GeoComponent,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
  ScatterChart,
  LineChart,
  BarChart,
  LinesChart,
  LabelLayout
])

export default {
  name: 'HomeView',
  data() {
    return {
      statistics: {
        dataMoney: {
          totalAmount: 0,
          monthlyAmount: 0,
          dailyAmount: 0
        },
        packStatistics: {
          total: 0,
          online: 0,
          make: 0
        },
        bssStatistics: {
          total: 0,
          online: 0,
          make: 0
        },
        userStatistics: {
          total: 0,
          active: 0,
          increase: 0
        }
      },
      exchangeRecords: [],
      refreshInterval: null,
      refreshCountdown: 30,
      countdownTimer: null,
      isFullscreen: false,
      exchangeChart: null,
      distributionMap: null,
      cabinetTypeChart: null,
      batteryTypeChart: null,
      mapHighlightTimer: null,
      exchangeChartTimer: null,
      distributionMapTimer: null,
      currentMapType: 'cabinet',
      mapTypes: [
        { type: 'cabinet', label: '电柜', count: 0 },
        { type: 'battery', label: '电池', count: 0 },
        { type: 'vehicle', label: '车辆', count: 0 },
        { type: 'store', label: '门店', count: 0 }
      ],
      scrollBoardConfig: {
        header: ['用户', '时间', '电柜'],
        data: [],
        rowNum: 14,
        headerBGC: 'rgba(255, 255, 255, 0.1)',
        oddRowBGC: 'rgba(255, 255, 255, 0.05)',
        evenRowBGC: 'rgba(255, 255, 255, 0.02)',
        waitTime: 1000,
        headerHeight: 35,
        rowHeight: 35,
        columnWidth: [80, 100, 130],
        align: ['left', 'center', 'center'],
        headerFontSize: 14,
        bodyFontSize: 13,
        headerColor: '#fff',
        bodyColor: 'rgba(255, 255, 255, 0.8)',
        carousel: 'single',
        hoverPause: true,
        index: false
      },
      scrollOffset: 0,
      scrollTimer: null,
      scrollStep: 1,
      displayRecords: [],
      isScrolling: true,
      scrollContentHeight: 0,
      isNoTransition: false
    }
  },
  computed: {
    statisticsList() {
      return [
        {
          title: '电柜总数',
          value: this.statistics.bssStatistics?.total || '-',
          type: 'status',
          extra: {
            online: this.statistics.bssStatistics?.online || '-',
            usage: this.statistics.bssStatistics?.make || '-'
          }
        },
        {
          title: '电池总数',
          value: this.statistics.packStatistics?.total || '-',
          type: 'status',
          extra: {
            online: this.statistics.packStatistics?.online || '-',
            usage: this.statistics.packStatistics?.make || '-'
          }
        },
        {
          title: '用户数据',
          value: this.statistics.userStatistics?.total || '-',
          type: 'user',
          extra: {
            active: this.statistics.userStatistics?.active || '-',
            new: this.statistics.userStatistics?.increase
          }
        },
        {
          title: '充值总额',
          value: this.formatMoney(this.statistics.dataMoney?.totalAmount),
          type: 'amount',
          extra: {
            today: this.statistics.dataMoney?.dailyAmount,
            month: this.statistics.dataMoney?.monthlyAmount
          }
        }
      ]
    },
    recordItemHeight() {
      return 116; // 记录项高度(100px) + 间距(16px)
    },
    totalScrollHeight() {
      // 总高度 = 单项高度 * 条数
      return this.recordItemHeight * this.displayRecords.length;
    }
  },

      async mounted() {
      echarts.registerMap('china', chinaMap)
      await this.fetchData()

      this.$nextTick(() => {
        this.initExchangeChart()
        this.initDistributionMap()
        this.initCabinetTypeChart()
        this.initBatteryTypeChart()
      })
      this.startAutoRefresh()
      this.initScroll()

      window.addEventListener('resize', this.handleResize)
      // 监听全屏状态变化
      document.addEventListener('fullscreenchange', this.handleFullscreenChange)
    },
  beforeDestroy() {
    // 停止所有定时器
    this.stopAllTimers()
    
    // 移除事件监听器
    window.removeEventListener('resize', this.handleResize)
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange)
    
    // 清理图表实例
    this.disposeChart('exchangeChart')
    this.disposeChart('distributionMap')
    this.disposeChart('cabinetTypeChart')
    this.disposeChart('batteryTypeChart')
  },
  methods: {
    async fetchData() {
      try {
        const response = await getStatistics()
        if (response.success && response.data) {
          this.statistics = response.data
          this.exchangeRecords = response.data.electricalChangeRecords || []
          this.scrollBoardConfig.data = this.exchangeRecords.map(record => [
            record.realName || record.nickName || '未知用户',
            this.formatTime(record.orderTime),
            record.cabId
          ])
          
          // 更新地图类型数据
          if (response.data.cityResource) {
            this.mapTypes = [
              { type: 'cabinet', label: '电柜', count: response.data.cityResource.cabinetTotal || 0 },
              { type: 'battery', label: '电池', count: response.data.cityResource.packTotal || 0 },
              { type: 'vehicle', label: '车辆', count: response.data.cityResource.vehicleTotal || 0 },
              { type: 'store', label: '门店', count: response.data.cityResource.storeTotal || 0 }
            ]
          }
          
          // 更新滚动数据
          this.updateScrollData()
          
          // 重新开始倒计时
          this.updateRefreshCountdown()
        }
      } catch (error) {
        // 检查是否是认证相关错误
        if (this.isAuthError(error)) {
          console.log('认证失败，停止定时器并准备跳转登录页')
          this.stopAllTimers()
          // 不显示错误消息，因为会跳转到登录页
          return
        }
        
        console.error('获取统计数据失败:', error)
        this.$message.error('获取数据失败，请检查网络连接')
        
        // 即使出错也要重新开始倒计时
        this.updateRefreshCountdown()
      }
    },
    initExchangeChart() {
      if (!this.$refs.exchangeChartRef) return
      this.exchangeChart = echarts.init(this.$refs.exchangeChartRef)

      // 使用真实数据
      const statistics = this.statistics.changeElectricityStatistics || []
      const dates = statistics.map(item => {
        const date = new Date(item.day)
        const month = (date.getMonth() + 1).toString().padStart(2, '0')
        const day = date.getDate().toString().padStart(2, '0')
        return `${month}-${day}`
      })
      const data = statistics.map(item => item.count)

      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          triggerOn: 'click',
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          borderColor: 'rgba(79, 172, 254, 0.4)',
          borderWidth: 1,
          padding: [10, 15],
          textStyle: {
            color: '#fff',
            fontSize: 13
          },
          formatter: (params) => {
            const value = params[0].value
            return `<div style="font-family: 'DIN Alternate'">
              <div style="font-size: 15px; color: #4facfe; margin-bottom: 10px; font-weight: 500">${params[0].name}</div>
              <div style="font-size: 13px; color: #fff; display: flex; justify-content: space-between; align-items: center">
                <span style="color: rgba(255,255,255,0.8)">换电次数：</span>
                <span style="color: #00ffff; font-weight: 500; margin-left: 12px">${value}</span>
              </div>
            </div>`
          }
        },
        grid: {
          top: 20,
          right: 20,
          bottom: 30,
          left: 40,
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: dates,
          axisLine: {
            lineStyle: {
              color: 'rgba(255, 255, 255, 0.3)'
            }
          },
          axisLabel: {
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: 12,
            rotate: 45,
            interval: 1
          }
        },
        yAxis: {
          type: 'value',
          name: '换电次数',
          nameTextStyle: {
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: 12,
            padding: [0, 0, 0, 40]
          },
          axisLine: {
            lineStyle: {
              color: 'rgba(255, 255, 255, 0.3)'
            }
          },
          axisLabel: {
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: 12
          },
          splitLine: {
            lineStyle: {
              color: 'rgba(255, 255, 255, 0.1)'
            }
          }
        },
        series: [{
          data: data,
          type: 'line',
          smooth: true,
          lineStyle: {
            color: '#00ffff',
            width: 2
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [{
              offset: 0,
              color: 'rgba(0, 255, 255, 0.3)'
            }, {
              offset: 1,
              color: 'rgba(0, 255, 255, 0.1)'
            }])
          },
          itemStyle: {
            color: '#00ffff',
            borderWidth: 2,
            borderColor: '#fff'
          }
        }]
      }
      this.exchangeChart.setOption(option)

      // 添加自动轮播
      this.clearTimer('exchangeChartTimer')
      let currentIndex = 0
      this.exchangeChartTimer = setInterval(() => {
        if (this.exchangeChart && dates.length > 0) {
          currentIndex = (currentIndex + 1) % dates.length
          this.exchangeChart.dispatchAction({
            type: 'showTip',
            seriesIndex: 0,
            dataIndex: currentIndex
          })
        }
      }, 3000)
    },
    initDistributionMap() {
      if (!this.$refs.distributionMapRef) return
      this.distributionMap = echarts.init(this.$refs.distributionMapRef)

      // 获取当前地图类型对应的数据
      const getCountByType = (city, type) => {
        if (!city) return 0
        switch (type) {
          case 'cabinet': return city.cabinetCount || 0
          case 'battery': return city.packCount || 0
          case 'vehicle': return city.vehicleCount || 0
          case 'store': return city.storeCount || 0
          default: return 0
        }
      }

      // 使用真实数据
      const cityResource = this.statistics.cityResource
      const cities = cityResource?.cityCount || []
      
      // 过滤有数据的城市
      const validCities = cities.filter(city => {
        const count = getCountByType(city, this.currentMapType)
        return count > 0 && cityCoords[city.city]
      })

      // 生成散点数据
      const scatterData = validCities.map(city => {
        const count = getCountByType(city, this.currentMapType)
        const coords = cityCoords[city.city]
        return {
          name: city.city,
          value: [...coords, count],
          symbolSize: Math.max(8, Math.min(20, count / 10 + 8)), // 根据数量调整大小
          itemStyle: {
            color: city.city === '长沙' ? '#ff0000' : '#4facfe',
            opacity: 0.8
          }
        }
      })

      // 生成飞线数据（从长沙到其他城市）
      const linesData = validCities
        .filter(city => city.city !== '长沙')
        .map(city => ({
          coords: [cityCoords['长沙'], cityCoords[city.city]],
          toCity: city.city
        }))

      const option = {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'item',
          triggerOn: 'mousemove',
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          borderColor: 'rgba(79, 172, 254, 0.4)',
          borderWidth: 1,
          padding: [10, 15],
          textStyle: {
            color: '#fff',
            fontSize: 13
          },
          formatter: (params) => {
            if (params.componentSubType === 'scatter') {
              const cityName = params.name
              const count = params.value[2]
              const cityData = cities.find(c => c.city === cityName)
              
              if (cityData) {
                const typeLabels = {
                  'cabinet': '电柜',
                  'battery': '电池',
                  'vehicle': '车辆',
                  'store': '门店'
                }
                const currentType = typeLabels[this.currentMapType] || '设备'
                
                return `<div style="font-family: 'DIN Alternate'">
                  <div style="font-size: 15px; color: #4facfe; margin-bottom: 10px; font-weight: 500">${cityName}</div>
                  <div style="font-size: 13px; color: #fff; line-height: 1.6">
                    <div style="display: flex; justify-content: space-between; align-items: center">
                      <span style="color: rgba(255,255,255,0.8)">${currentType}数量：</span>
                      <span style="color: #00ffff; font-weight: 500; margin-left: 12px">${count}</span>
                    </div>
                  </div>
                </div>`
              }
              
              return `<div style="font-family: 'DIN Alternate'">
                <div style="font-size: 15px; color: #4facfe; margin-bottom: 10px; font-weight: 500">${params.name}</div>
                <div style="font-size: 13px; color: #fff; display: flex; justify-content: space-between; align-items: center">
                  <span style="color: rgba(255,255,255,0.8)">设备数量：</span>
                  <span style="color: #00ffff; font-weight: 500; margin-left: 12px">${params.value[2]}</span>
                </div>
              </div>`
            } else if (params.componentSubType === 'lines') {
              return `<div style="font-family: 'DIN Alternate'">
                <div style="font-size: 15px; color: #4facfe; margin-bottom: 10px; font-weight: 500">连接线</div>
                <div style="font-size: 13px; color: #fff">
                  <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px">
                    <span style="color: rgba(255,255,255,0.8)">起点：</span>
                    <span style="color: #ff3333; font-weight: 500; margin-left: 12px">长沙</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; align-items: center">
                    <span style="color: rgba(255,255,255,0.8)">终点：</span>
                    <span style="color: #00ffff; font-weight: 500; margin-left: 12px">${params.data.toCity}</span>
                  </div>
                </div>
              </div>`
            }
            return `<div style="font-family: 'DIN Alternate'">
              <div style="font-size: 15px; color: #4facfe; margin-bottom: 6px; font-weight: 500">${params.name}</div>
              <div style="font-size: 13px; color: rgba(255,255,255,0.9)">
                <span style="color: #00ffff; font-weight: 500">${params.value || '-'}</span>
              </div>
            </div>`
          }
        },
        geo: {
          show: true,
          map: 'china',
          roam: false,
          zoom: 1.2,
          center: [105, 35],
          itemStyle: {
            color: '#1b1b4b',
            opacity: 0.8,
            borderWidth: 1,
            borderColor: '#4facfe'
          },
          label: {
            show: true,
            color: 'rgba(255, 255, 255, 0.8)',
            fontSize: 10,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            padding: [3, 5],
            borderRadius: 3
          },
          emphasis: {
            label: {
              show: true,
              color: '#fff',
              fontSize: 11,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              padding: [4, 6],
              borderRadius: 3
            },
            itemStyle: {
              areaColor: '#4facfe',
              opacity: 0.8
            }
          },
          select: {
            label: {
              show: true,
              color: '#fff',
              fontSize: 12
            },
            itemStyle: {
              areaColor: '#4facfe'
            }
          }
        },
        series: [{
          type: 'scatter',
          coordinateSystem: 'geo',
          symbol: 'circle',
          itemStyle: {
            borderWidth: 1,
            borderColor: '#fff'
          },
          data: scatterData
        },
        {
          name: '飞线',
          type: 'lines',
          coordinateSystem: 'geo',
          zlevel: 2,
          effect: {
            show: true,
            period: 4,
            trailLength: 0.4,
            symbol: 'arrow',
            symbolSize: 6,
            loop: true
          },
          lineStyle: {
            width: 2,
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [{
              offset: 0,
              color: '#58B3CC'
            }, {
              offset: 1,
              color: '#F58158'
            }]),
            opacity: 0.8,
            curveness: 0.3
          },
          data: linesData
        }]
      }

      this.distributionMap.setOption(option)

      // 添加自动轮播
      this.clearTimer('distributionMapTimer')
      
      if (scatterData.length > 0) {
        let currentIndex = 0
        this.distributionMapTimer = setInterval(() => {
          if (this.distributionMap && scatterData.length > 0) {
            currentIndex = (currentIndex + 1) % scatterData.length
            this.distributionMap.dispatchAction({
              type: 'showTip',
              seriesIndex: 0,
              dataIndex: currentIndex
            })
          }
        }, 3000)
      }
    },
    initCabinetTypeChart() {
      if (!this.$refs.cabinetTypeChartRef) return
      this.cabinetTypeChart = echarts.init(this.$refs.cabinetTypeChartRef)
      
      // 使用真实数据
      const bssTypes = this.statistics.bssTypes || []
      const types = bssTypes.map(item => item.type)
      const data = bssTypes.map(item => item.count)
      
      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          triggerOn: 'mousemove',
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          borderColor: 'rgba(79, 172, 254, 0.4)',
          borderWidth: 1,
          padding: [10, 15],
          textStyle: {
            color: '#fff',
            fontSize: 13
          },
          formatter: (params) => {
            const value = params[0].value
            return `<div style="font-family: 'DIN Alternate'">
              <div style="font-size: 15px; color: #4facfe; margin-bottom: 10px; font-weight: 500">${params[0].name}</div>
              <div style="font-size: 13px; color: #fff; display: flex; justify-content: space-between; align-items: center">
                <span style="color: rgba(255,255,255,0.8)">数量：</span>
                <span style="color: #00ffff; font-weight: 500; margin-left: 12px">${value}</span>
              </div>
            </div>`
          }
        },
        grid: {
          top: 20,
          right: 20,
          bottom: 30,
          left: 40,
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: types,
          axisLine: {
            lineStyle: {
              color: 'rgba(255, 255, 255, 0.3)'
            }
          },
          axisLabel: {
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: 12
          }
        },
        yAxis: {
          type: 'value',
          axisLine: {
            lineStyle: {
              color: 'rgba(255, 255, 255, 0.3)'
            }
          },
          axisLabel: {
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: 12
          },
          splitLine: {
            lineStyle: {
              color: 'rgba(255, 255, 255, 0.1)'
            }
          }
        },
        series: [{
          data: data,
          type: 'bar',
          barWidth: '40%',
          itemStyle: {
            color: function(params) {
              const colors = ['#00ffff', '#00ff99', '#00ff33', '#00cc00', '#0099ff', '#0066ff', '#0033ff']
              return colors[params.dataIndex % colors.length]
            }
          }
        }]
      }
      this.cabinetTypeChart.setOption(option)
    },
    initBatteryTypeChart() {
      if (!this.$refs.batteryTypeChartRef) return
      this.batteryTypeChart = echarts.init(this.$refs.batteryTypeChartRef)
      
      // 使用真实数据
      const packTypes = this.statistics.packTypes || []
      const types = packTypes.map(item => item.type)
      const data = packTypes.map(item => item.count)
      
      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          triggerOn: 'mousemove',
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          borderColor: 'rgba(79, 172, 254, 0.4)',
          borderWidth: 1,
          padding: [10, 15],
          textStyle: {
            color: '#fff',
            fontSize: 13
          },
          formatter: (params) => {
            const value = params[0].value
            return `<div style="font-family: 'DIN Alternate'">
              <div style="font-size: 15px; color: #4facfe; margin-bottom: 10px; font-weight: 500">${params[0].name}</div>
              <div style="font-size: 13px; color: #fff; display: flex; justify-content: space-between; align-items: center">
                <span style="color: rgba(255,255,255,0.8)">数量：</span>
                <span style="color: #00ffff; font-weight: 500; margin-left: 12px">${value}</span>
              </div>
            </div>`
          }
        },
        grid: {
          top: 20,
          right: 20,
          bottom: 30,
          left: 40,
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: types,
          axisLine: {
            lineStyle: {
              color: 'rgba(255, 255, 255, 0.3)'
            }
          },
          axisLabel: {
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: 12
          }
        },
        yAxis: {
          type: 'value',
          axisLine: {
            lineStyle: {
              color: 'rgba(255, 255, 255, 0.3)'
            }
          },
          axisLabel: {
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: 12
          },
          splitLine: {
            lineStyle: {
              color: 'rgba(255, 255, 255, 0.1)'
            }
          }
        },
        series: [{
          data: data,
          type: 'bar',
          barWidth: '40%',
          itemStyle: {
            color: function(params) {
              const colors = ['#ff00ff', '#ff0099', '#ff0033', '#cc0000', '#9900ff', '#6600ff', '#3300ff']
              return colors[params.dataIndex % colors.length]
            }
          }
        }]
      }
      this.batteryTypeChart.setOption(option)
    },
    startAutoRefresh() {
      // 清除之前的定时器
      this.clearTimer('refreshInterval')
      
      // 设置30秒倒计时
      this.refreshInterval = setInterval(() => {
        this.fetchData()
      }, 30000)
      
      // 只在初始化时开始倒计时显示
      this.updateRefreshCountdown()
    },
    
    updateRefreshCountdown() {
      // 清除之前的倒计时定时器
      this.clearTimer('countdownTimer')
      
      let countdown = 30
      this.refreshCountdown = countdown
      
      this.countdownTimer = setInterval(() => {
        countdown--
        this.refreshCountdown = countdown
        
        if (countdown <= 0) {
          this.clearTimer('countdownTimer')
          // 倒计时结束后，等待下一次自动刷新
          this.refreshCountdown = 30
        }
      }, 1000)
    },
    stopAutoRefresh() {
      this.clearTimer('refreshInterval')
      // 重置倒计时
      this.refreshCountdown = 30
    },

    formatMoney(amount) {
      if (amount == null) return '-'
      return `¥ ${amount.toFixed(2)}`
    },
    formatTime(timeStr) {
      const date = new Date(timeStr)
      const month = (date.getMonth() + 1).toString().padStart(2, '0')
      const day = date.getDate().toString().padStart(2, '0')
      const hour = date.getHours().toString().padStart(2, '0')
      const minute = date.getMinutes().toString().padStart(2, '0')
      return `${month}-${day} ${hour}:${minute}`
    },
    switchMapType(type) {
      if (!type || this.currentMapType === type) {
        return // 避免重复更新
      }
      
      console.log(`切换地图类型: ${this.currentMapType} -> ${type}`)
      this.currentMapType = type
      
      // 使用 nextTick 确保 DOM 更新完成后再更新地图
      this.$nextTick(() => {
        this.updateMapData(type)
      })
    },
    updateMapData(type) {
      if (!this.distributionMap) {
        console.warn('地图实例不存在，无法更新地图数据')
        return
      }

      // 使用真实数据
      const cityResource = this.statistics.cityResource
      if (!cityResource || !cityResource.cityCount || !Array.isArray(cityResource.cityCount)) {
        console.warn('城市资源数据无效，无法更新地图数据')
        return
      }

      // 根据类型获取对应的数据字段
      const getCountByType = (city, type) => {
        if (!city) return 0
        switch (type) {
          case 'cabinet': return city.cabinetCount || 0
          case 'battery': return city.packCount || 0
          case 'vehicle': return city.vehicleCount || 0
          case 'store': return city.storeCount || 0
          default: return 0
        }
      }

      // 构建目标城市数据
      const targetCities = []
      cityResource.cityCount.forEach(city => {
        if (city && city.city && city.city !== '长沙') {
          const coords = cityCoords[city.city]
          if (coords) {
            targetCities.push({
              name: city.city,
              coords: coords,
              value: getCountByType(city, type)
            })
          }
        }
      })

      // 构建中心城市数据
      const changshaCity = cityResource.cityCount.find(city => city && city.city === '长沙')
      const centerCity = {
        name: '长沙',
        coords: cityCoords['长沙'] || [112.982279, 28.19409],
        value: getCountByType(changshaCity, type)
      }

      // 构建飞线数据
      const linesData = targetCities.map(city => ({
        coords: [centerCity.coords, city.coords],
        toCity: city.name
      }))

      // 添加调试信息
      console.log(`更新地图数据 - 类型: ${type}, 目标城市数量: ${targetCities.length}, 中心城市: ${centerCity.name}`)

      try {
        this.distributionMap.setOption({
        tooltip: {
          trigger: 'item',
          triggerOn: 'mousemove',
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          borderColor: 'rgba(79, 172, 254, 0.4)',
          borderWidth: 1,
          padding: [10, 15],
          textStyle: {
            color: '#fff',
            fontSize: 13
          },
          formatter: (params) => {
            if (params.componentSubType === 'scatter') {
              const cityName = params.name
              const count = params.value[2]
              const cityData = cityResource.cityCount.find(c => c.city === cityName)
              
              if (cityData) {
                const typeLabels = {
                  'cabinet': '电柜',
                  'battery': '电池',
                  'vehicle': '车辆',
                  'store': '门店'
                }
                const currentType = typeLabels[type] || '设备'
                
                return `<div style="font-family: 'DIN Alternate'">
                  <div style="font-size: 15px; color: #4facfe; margin-bottom: 10px; font-weight: 500">${cityName}</div>
                  <div style="font-size: 13px; color: #fff; line-height: 1.6">
                    <div style="display: flex; justify-content: space-between; align-items: center">
                      <span style="color: rgba(255,255,255,0.8)">${currentType}数量：</span>
                      <span style="color: #00ffff; font-weight: 500; margin-left: 12px">${count}</span>
                    </div>
                  </div>
                </div>`
              }
              
              return `<div style="font-family: 'DIN Alternate'">
                <div style="font-size: 15px; color: #4facfe; margin-bottom: 10px; font-weight: 500">${params.name}</div>
                <div style="font-size: 13px; color: #fff; display: flex; justify-content: space-between; align-items: center">
                  <span style="color: rgba(255,255,255,0.8)">设备数量：</span>
                  <span style="color: #00ffff; font-weight: 500; margin-left: 12px">${params.value[2]}</span>
                </div>
              </div>`
            } else if (params.componentSubType === 'lines') {
              return `<div style="font-family: 'DIN Alternate'">
                <div style="font-size: 15px; color: #4facfe; margin-bottom: 10px; font-weight: 500">连接线</div>
                <div style="font-size: 13px; color: #fff">
                  <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px">
                    <span style="color: rgba(255,255,255,0.8)">起点：</span>
                    <span style="color: #ff3333; font-weight: 500; margin-left: 12px">长沙</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; align-items: center">
                    <span style="color: rgba(255,255,255,0.8)">终点：</span>
                    <span style="color: #00ffff; font-weight: 500; margin-left: 12px">${params.data.toCity}</span>
                  </div>
                </div>
              </div>`
            }
            return `<div style="font-family: 'DIN Alternate'">
              <div style="font-size: 15px; color: #4facfe; margin-bottom: 6px; font-weight: 500">${params.name}</div>
              <div style="font-size: 13px; color: rgba(255,255,255,0.9)">
                <span style="color: #00ffff; font-weight: 500">${params.value || '-'}</span>
              </div>
            </div>`
          }
        },
        geo: {
          show: true,
          map: 'china',
          roam: false,
          zoom: 1.2,
          center: [105, 35],
          itemStyle: {
            color: '#1b1b4b',
            opacity: 0.8,
            borderWidth: 1,
            borderColor: '#4facfe'
          },
          label: {
            show: true,
            color: 'rgba(255, 255, 255, 0.8)',
            fontSize: 10,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            padding: [3, 5],
            borderRadius: 3
          },
          emphasis: {
            label: {
              show: true,
              color: '#fff',
              fontSize: 11,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              padding: [4, 6],
              borderRadius: 3
            },
            itemStyle: {
              areaColor: '#4facfe',
              opacity: 0.8
            }
          },
          select: {
            label: {
              show: true,
              color: '#fff',
              fontSize: 12
            },
            itemStyle: {
              areaColor: '#4facfe'
            }
          }
        },
        series: [
          {
            name: '散点',
            type: 'scatter',
            coordinateSystem: 'geo',
            data: [
              {
                name: centerCity.name,
                value: [...centerCity.coords, centerCity.value],
                itemStyle: {
                  color: '#ff0000',
                  opacity: 0.8
                },
                symbolSize: 15,
                emphasis: {
                  itemStyle: {
                    color: '#ff3333',
                    opacity: 1
                  }
                }
              },
              ...targetCities.map(city => ({
                name: city.name,
                value: [...city.coords, city.value],
                symbolSize: 8,
                itemStyle: {
                  color: '#00ffff',
                  opacity: 0.8
                },
                emphasis: {
                  itemStyle: {
                    color: '#33ffff',
                    opacity: 1
                  }
                }
              }))
            ]
          },
          {
            name: '飞线',
            type: 'lines',
            coordinateSystem: 'geo',
            zlevel: 2,
            effect: {
              show: true,
              period: 4,
              trailLength: 0.4,
              symbol: 'arrow',
              symbolSize: 6,
              loop: true
            },
            lineStyle: {
              width: 2,
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [{
                offset: 0,
                color: '#58B3CC'
              }, {
                offset: 1,
                color: '#F58158'
              }]),
              opacity: 0.8,
              curveness: 0.3
            },
            data: linesData
          }
        ]
        }, true) // 使用 notMerge: true 确保完全替换配置

        // 更新自动轮播
        this.clearTimer('distributionMapTimer')
        
        const scatterData = [
          { name: centerCity.name, value: centerCity.value },
          ...targetCities.map(city => ({ name: city.name, value: city.value }))
        ]
        
        if (scatterData.length > 0) {
          let currentIndex = 0
          this.distributionMapTimer = setInterval(() => {
            if (this.distributionMap && scatterData.length > 0) {
              currentIndex = (currentIndex + 1) % scatterData.length
              this.distributionMap.dispatchAction({
                type: 'showTip',
                seriesIndex: 0,
                dataIndex: currentIndex
              })
            }
          }, 3000)
        }

        console.log('地图数据更新成功')
      } catch (error) {
        console.error('更新地图数据失败:', error)
      }
    },
    handleResize() {
      this.$nextTick(() => {
        if (this.exchangeChart) {
          this.exchangeChart.resize()
        }
        if (this.distributionMap) {
          this.distributionMap.resize()
        }
        if (this.cabinetTypeChart) {
          this.cabinetTypeChart.resize()
        }
        if (this.batteryTypeChart) {
          this.batteryTypeChart.resize()
        }
        // 处理 DataV 组件的 resize
        const borderBoxes = [
          this.$refs.statisticsBorderBox,
          this.$refs.recordsBorderBox,
          this.$refs.mapBorderBox,
          this.$refs.chartBorderBox,
          this.$refs.typeBorderBox
        ]
        borderBoxes.forEach(box => {
          if (box && box.initWH) {
            box.initWH()
          }
        })
      })
    },
    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen
      if (this.isFullscreen) {
        document.documentElement.requestFullscreen()
      } else {
        document.exitFullscreen()
      }
      
      // 全屏模式切换后重新调整图表大小
      this.$nextTick(() => {
        setTimeout(() => {
          this.handleResize()
        }, 300) // 等待全屏动画完成
      })
    },
    handleLogout() {
      this.$confirm('确认退出登录？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        // 清除认证状态
        Cookies.remove('satoken')
        // 跳转到登录页
        this.$router.push('/login')
      }).catch(() => {
        this.$message({
          type: 'info',
          message: '已取消退出'
        })
      })
    },
    initScroll() {
      // 初始化显示记录，拼接一份副本用于无缝滚动
      this.displayRecords = [...this.exchangeRecords, ...this.exchangeRecords];
      this.$nextTick(() => {
        this.scrollContentHeight = this.recordItemHeight * this.exchangeRecords.length;
      });
      this.startAutoScroll();
    },
    updateScrollData() {
      // 更新滚动数据
      this.displayRecords = [...this.exchangeRecords, ...this.exchangeRecords];
      this.$nextTick(() => {
        this.scrollContentHeight = this.recordItemHeight * this.exchangeRecords.length;
      });
    },
    startAutoScroll() {
      this.clearTimer('scrollTimer');
      this.scrollTimer = setInterval(() => {
        if (!this.isScrolling) return;
        this.scrollOffset += this.scrollStep;
        // 到达副本第一个时，先有动画滑动，动画结束后无动画瞬间回到顶部
        if (this.scrollOffset >= this.scrollContentHeight) {
          this.isNoTransition = true;
          this.scrollOffset = 0;
          // 下一帧再恢复动画
          this.$nextTick(() => {
            setTimeout(() => {
              this.isNoTransition = false;
            }, 50);
          });
        }
      }, 50);
    },
    stopAutoScroll() {
      this.clearTimer('scrollTimer');
    },
    stopAllTimers() {
      // 停止所有定时器
      this.stopAutoRefresh()
      this.stopAutoScroll()
      
      // 清理所有定时器
      this.clearTimer('countdownTimer')
      this.clearTimer('exchangeChartTimer')
      this.clearTimer('distributionMapTimer')
    },
    // 统一的定时器清理方法
    clearTimer(timerName) {
      if (this[timerName]) {
        clearInterval(this[timerName])
        this[timerName] = null
      }
    },
    // 统一的图表清理方法
    disposeChart(chartName) {
      if (this[chartName]) {
        this[chartName].dispose()
        this[chartName] = null
      }
    },
    handleMouseEnter() {
      this.isScrolling = false;
    },
    handleMouseLeave() {
      this.isScrolling = true;
    },
    // 检查是否是认证相关错误
    isAuthError(error) {
      if (!error || !error.message) return false;
      const authErrorKeywords = ['未登录', 'token过期', '未授权', 'token 失效', '认证失败'];
      return authErrorKeywords.some(keyword => error.message.includes(keyword));
    },
    // 处理全屏状态变化
    handleFullscreenChange() {
      const isFullscreen = !!document.fullscreenElement
      if (this.isFullscreen !== isFullscreen) {
        this.isFullscreen = isFullscreen
        // 全屏状态变化后重新调整图表大小
        this.$nextTick(() => {
          setTimeout(() => {
            this.handleResize()
          }, 100)
        })
      }
    }
  }
}
</script>

<style lang="scss"> 
</style> 